import type * as React from 'react'
import { forwardRef } from 'react'

import { useDraggable, useDroppable } from '@dnd-kit/core'
import clsx from 'clsx'

import {
  ArrowDownUpIcon,
  Badge,
  GripVerticalIcon,
  IconButton,
} from '@buffer-mono/popcorn'

import {
  PostCard,
  PostCardCompact,
  PostCard_Post,
} from '~publish/components/PostCard'
import { readFragment } from '~publish/graphql'
import type { FragmentOf } from '~publish/graphql'
import useDraggingCursor from '~publish/hooks/useDraggingCursor'

import styles from './DraggablePostCard.module.css'

type DraggablePostCardStyle = React.CSSProperties &
  Record<`--${string}`, string | number | undefined>

type DraggablePostCardProps = {
  post: FragmentOf<typeof PostCard_Post>
  inOverlay?: boolean
  className?: string
  style?: React.CSSProperties
}

export const DraggablePostCard = forwardRef<
  HTMLDivElement,
  DraggablePostCardProps
>(({ post: passedPost, className, style, inOverlay = false }, forwardedRef) => {
  const post = readFragment(PostCard_Post, passedPost)
  const {
    attributes,
    listeners,
    setNodeRef: setDraggableNodeRef,
    transform,
    isDragging,
  } = useDraggable({
    id: post.id,
    data: { postId: post.id, type: 'post', dueAt: post.dueAt },
  })
  const {
    setNodeRef: setDroppableNodeRef,
    isOver,
    active,
  } = useDroppable({
    id: post.id,
    data: { postId: post.id, type: 'post', dueAt: post.dueAt },
  })
  useDraggingCursor(isDragging)

  const isDraggedPost = isDragging && inOverlay
  const showDropOverlay = isOver && active?.id !== post.id
  const { onKeyDown, ...mouseListeners } = listeners ?? {}

  const draggablePostCardStyle: DraggablePostCardStyle = {
    '--translate-x':
      isDraggedPost && transform ? `${transform.x}px` : undefined,
    '--translate-y':
      isDraggedPost && transform ? `${transform.y}px` : undefined,
    '--scale-x':
      isDraggedPost && transform?.scaleX ? `${transform.scaleX}` : undefined,
    '--scale-y':
      isDraggedPost && transform?.scaleY ? `${transform.scaleY}` : undefined,
    ...style,
  }

  return (
    <div
      ref={(node): void => {
        setDroppableNodeRef(node)
        setDraggableNodeRef(node)
        if (typeof forwardedRef === 'function') forwardedRef(node)
        else if (forwardedRef) forwardedRef.current = node
      }}
      className={clsx(
        styles.base,
        {
          [styles.dragging]: isDragging,
          [styles.dragOverlay]: inOverlay,
          [styles.dragOver]: showDropOverlay,
        },
        className,
      )}
      style={draggablePostCardStyle}
      {...mouseListeners}
    >
      {showDropOverlay && (
        <div className={styles.swapOverlay}>
          <Badge className={styles.swapBadge} size="large">
            <ArrowDownUpIcon /> Swap posts
          </Badge>
        </div>
      )}
      <IconButton
        variant="tertiary"
        size="small"
        label="Drag to reschedule"
        className={styles.dragHandle}
        {...attributes}
        onKeyDown={onKeyDown ? (event): void => onKeyDown(event) : undefined}
        data-dnd
      >
        <GripVerticalIcon />
      </IconButton>
      {!inOverlay && isDragging && <div className={styles.originalPosition} />}
      {inOverlay ? (
        <PostCardCompact
          post={passedPost}
          className={styles.postCardDragOverlay}
        />
      ) : (
        <PostCard post={passedPost} draggable={!showDropOverlay} rounded="lg" />
      )}
    </div>
  )
})

DraggablePostCard.displayName = 'DraggablePostCard'
